import React, { useState } from 'react';
import { CheckCircle2, Circle, Loader2, AlertCircle, FileSearch, Database, Network, LineChart, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { ResearchStep, ResearchSession } from '../types';

interface ResearchProgressProps {
  session: ResearchSession;
}

export default function ResearchProgress({ session }: ResearchProgressProps) {
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>({
    'planning-step': true,
  });

  const toggleStep = (id: string) => {
    setExpandedSteps(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getStepIcon = (type: string, status: string) => {
    if (status === 'running') {
      return <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />;
    }
    if (status === 'completed') {
      return <CheckCircle2 className="w-4 h-4 text-cyan-400 fill-cyan-950/20" />;
    }
    if (status === 'failed') {
      return <AlertCircle className="w-4 h-4 text-rose-500 fill-rose-950/20" />;
    }

    switch (type) {
      case 'planning':
        return <Network className="w-4 h-4 text-slate-500" />;
      case 'search':
        return <FileSearch className="w-4 h-4 text-slate-500" />;
      case 'extract':
        return <Database className="w-4 h-4 text-slate-500" />;
      case 'analyze':
        return <LineChart className="w-4 h-4 text-slate-500" />;
      default:
        return <Circle className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStatusMessage = (status: string, lang: string) => {
    switch (status) {
      case 'planning':
        return lang === 'uk' ? 'Планування дослідження...' : 'Planning research design...';
      case 'searching':
        return lang === 'uk' ? 'Сканування інтернету та збір даних...' : 'Scanning web and collecting data...';
      case 'analyzing':
        return lang === 'uk' ? 'Нейромережевий аналіз фактів...' : 'Analyzing facts with neural networks...';
      case 'synthesizing':
        return lang === 'uk' ? 'Синтез структурованого звіту...' : 'Synthesizing report sections...';
      case 'completed':
        return lang === 'uk' ? 'Аналіз завершено!' : 'Research complete!';
      case 'failed':
        return lang === 'uk' ? 'Сталася помилка.' : 'Research failed.';
      default:
        return lang === 'uk' ? 'Очікування...' : 'Idle...';
    }
  };

  const totalSources = session.steps.reduce((acc, step) => {
    if (step.payload?.sources) {
      return acc + step.payload.sources.length;
    }
    return acc;
  }, 0);

  const totalFacts = session.steps.reduce((acc, step) => {
    if (step.payload?.facts) {
      return acc + step.payload.facts.length;
    }
    return acc;
  }, 0);

  const lang = session.options.language;

  return (
    <div id="research-progress" className="w-full bg-white border border-slate-200 rounded-lg p-5 relative shadow-xs">
      {/* Upper Status Panel */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-4 border-b border-slate-200 mb-4">
        <div>
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold font-mono rounded bg-red-500/10 text-red-500 border border-red-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            {lang === 'uk' ? 'ГЛИБОКИЙ_ПОШУК_АКТИВНИЙ' : 'DEEP_SEARCH_ACTIVE'}
          </span>
          <h3 className="text-sm font-bold font-mono text-slate-800 mt-2 uppercase tracking-wide">
            {getStatusMessage(session.status, lang)}
          </h3>
          <p className="text-[10px] font-mono text-slate-400 mt-1 max-w-lg line-clamp-1">
            {lang === 'uk' ? 'Тема: ' : 'Subject: '} "{session.query}"
          </p>
        </div>

        {/* Big circular or bar progress meter */}
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-[10px] font-bold font-mono text-slate-400 uppercase tracking-wider">
              {lang === 'uk' ? 'ПРОГРЕС' : 'PROGRESS'}
            </p>
            <p className="text-xl font-bold font-mono text-red-500">
              {session.progress}%
            </p>
          </div>
          <div className="w-24 h-1.5 bg-slate-100 rounded overflow-hidden relative border border-slate-200">
            <div
              className="h-full bg-red-500 rounded transition-all duration-500 ease-out shadow-[0_0_8px_rgba(239,68,68,0.3)]"
              style={{ width: `${session.progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Analytics stats overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <div className="p-3 rounded bg-slate-50 border border-slate-200">
          <p className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">
            {lang === 'uk' ? 'Індексовано сайтів' : 'Indexed Web Sources'}
          </p>
          <p className="text-lg font-bold font-mono text-slate-800 mt-1 flex items-center gap-2">
            <Globe className="w-4 h-4 text-red-500" />
            {totalSources}
          </p>
        </div>
        <div className="p-3 rounded bg-slate-50 border border-slate-200">
          <p className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">
            {lang === 'uk' ? 'Вилучено підтверджених фактів' : 'Extracted Neural Facts'}
          </p>
          <p className="text-lg font-bold font-mono text-slate-800 mt-1 flex items-center gap-2">
            <Database className="w-4 h-4 text-red-500" />
            {totalFacts}
          </p>
        </div>
        <div className="col-span-2 sm:col-span-1 p-3 rounded bg-slate-50 border border-slate-200">
          <p className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">
            {lang === 'uk' ? 'Індекс точності джерел' : 'Grounding Confidence'}
          </p>
          <p className="text-lg font-bold font-mono text-red-500 mt-1 flex items-center gap-2">
            99.2%
          </p>
        </div>
      </div>

      {/* Steps Timeline */}
      <div className="space-y-3 relative pl-4 sm:pl-6 border-l border-slate-200 ml-2.5">
        {session.steps.map((step, idx) => {
          const isExpanded = expandedSteps[step.id];
          return (
            <div key={step.id} className="relative group">
              {/* Timeline dot node */}
              <div className="absolute -left-[27px] sm:-left-[35px] top-1.5 bg-white p-1 rounded border border-slate-200 shadow-xs">
                {getStepIcon(step.type, step.status)}
              </div>

              <div className="bg-slate-50 border border-slate-200 hover:border-slate-300 rounded p-3 transition-all duration-150">
                <div 
                  className="flex items-center justify-between gap-4 cursor-pointer"
                  onClick={() => toggleStep(step.id)}
                >
                  <div>
                    <h4 className="text-[11px] font-bold font-mono text-slate-800 flex items-center gap-2 uppercase tracking-wide">
                      {step.title}
                      {step.status === 'running' && (
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-ping" />
                      )}
                    </h4>
                    <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed font-mono">
                      {step.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 font-mono shrink-0">
                    <span className="text-[9px] text-slate-400">
                      {step.timestamp}
                    </span>
                    {step.payload && (
                      isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Collapsible detail box for facts and sources */}
                {isExpanded && step.payload && (
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-3 font-mono">
                    {/* Hypotheses / Planning Payload */}
                    {step.payload.hypotheses && (
                      <div className="space-y-1">
                        <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                          {lang === 'uk' ? 'Робочі гіпотези:' : 'Working Hypotheses:'}
                        </p>
                        <ul className="space-y-1">
                          {step.payload.hypotheses.map((hyp: string, hIdx: number) => (
                            <li key={hIdx} className="text-[10px] text-slate-600 pl-4 relative before:content-[''] before:absolute before:left-1 before:top-2 before:w-1 before:h-1 before:bg-red-500 before:rounded-full leading-relaxed">
                              {hyp}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Facts Payload */}
                    {step.payload.facts && (
                      <div className="space-y-1">
                        <p className="text-[9px] font-bold uppercase tracking-wider text-red-500">
                          {lang === 'uk' ? 'Вилучені дані:' : 'Extracted Data Points:'}
                        </p>
                        <ul className="space-y-1.5">
                          {step.payload.facts.map((fact: string, fIdx: number) => (
                            <li key={fIdx} className="text-[10px] text-slate-600 bg-white border border-slate-200 p-2 rounded leading-relaxed">
                              {fact}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Sources Payload */}
                    {step.payload.sources && step.payload.sources.length > 0 && (
                      <div className="space-y-1 pt-1">
                        <p className="text-[9px] font-bold uppercase tracking-wider text-red-500">
                          {lang === 'uk' ? 'Джерела цитування:' : 'Cited References:'}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {step.payload.sources.map((src: any, sIdx: number) => (
                            <a
                              key={sIdx}
                              href={src.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[9px] text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded transition-colors"
                            >
                              <Globe className="w-3 h-3 text-red-500" />
                              <span className="font-bold text-slate-700 truncate max-w-[100px]">{src.domain}</span>
                              <span className="text-slate-400 truncate max-w-[120px]">— {src.title}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Loading bottom overlay */}
      {session.status !== 'completed' && session.status !== 'failed' && (
        <div className="mt-6 flex items-center justify-center gap-2 py-3 text-[10px] font-bold uppercase font-mono text-slate-400 bg-slate-50 rounded border border-slate-200">
          <Loader2 className="w-3.5 h-3.5 text-red-500 animate-spin" />
          <span>{lang === 'uk' 
            ? 'ТРИВАЄ_СИНТЕЗ_ІНТЕЛЕКТУАЛЬНОГО_ЗВІТУ...' 
            : 'COGNITIVE_SYNTHESIS_IN_PROGRESS...'}</span>
        </div>
      )}
    </div>
  );
}
